import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { COL, ensureSpreadsheet, readAllRows } from "../api/sheets";
import { useViewWidth } from "../components/ViewWidthSlider";
import { useT } from "../i18n";
import { Row } from "../types/sheets";
import {
  analyzeTasks,
  MISSING_PLACEHOLDER,
  TaskGroup,
  TaskKey,
  TaskRow,
} from "../utils/taskTypes";

const GROUP_META: Record<TaskKey, { icon: string; color: string }> = {
  missingId: { icon: "fa-id-card", color: "text-rose-600" },
  missingLocation: { icon: "fa-location-dot", color: "text-amber-600" },
  missingCoords: { icon: "fa-map-location-dot", color: "text-blue-600" },
  missingInstallSpot: { icon: "fa-sign-hanging", color: "text-purple-600" },
};

// The row's stable handle for the deep link: ID when present, otherwise its # (column A),
// which is populated exactly for the rows that lack an ID. Array index is a last-resort fallback.
function editLink(tr: TaskRow): string {
  const id = String(tr.row[COL["ID"]] ?? "").trim();
  if (id) return `/?editId=${encodeURIComponent(id)}`;
  const no = String(tr.row[COL["#"]] ?? "").trim();
  if (no) return `/?editNo=${encodeURIComponent(no)}`;
  return `/?editIdx=${tr.idx}`;
}

function rowLabel(tr: TaskRow): string {
  const id = String(tr.row[COL["ID"]] ?? "").trim();
  if (id) return id;
  const no = String(tr.row[COL["#"]] ?? "").trim();
  return `#${no || tr.idx + 1}`;
}

function rowContext(tr: TaskRow): string[] {
  return [
    tr.row[COL["キャンパス"]],
    tr.row[COL["建物"]],
    tr.row[COL["部局、施設名"]],
    tr.row[COL["担当者"]],
  ]
    .map((v) => String(v ?? "").trim())
    .filter((s) => s && s !== MISSING_PLACEHOLDER);
}

function GroupBlock({ group, t }: { group: TaskGroup; t: ReturnType<typeof useT> }) {
  const meta = GROUP_META[group.key];
  return (
    <section className="border rounded-lg overflow-hidden">
      <header className="bg-gray-50 border-b px-3 py-2">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <i className={`fa-solid ${meta.icon} ${meta.color}`} aria-hidden="true" />
          <span>{t(`tasks.group.${group.key}.title`)}</span>
          <span className="ml-auto text-xs font-normal text-gray-500 tabular-nums">
            {t("tasks.count", { n: String(group.rows.length) })}
          </span>
        </h3>
        <p className="mt-1 text-xs text-gray-600">{t(`tasks.group.${group.key}.desc`)}</p>
      </header>
      <ul className="divide-y">
        {group.rows.map((tr) => {
          const ctx = rowContext(tr);
          return (
            <li key={`${group.key}-${tr.idx}`} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm text-gray-800 truncate">{rowLabel(tr)}</div>
                <div className="text-xs text-gray-500 truncate">{ctx.length ? ctx.join(" / ") : "—"}</div>
              </div>
              <Link
                to={editLink(tr)}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs bg-pink-600 hover:bg-pink-700 text-white rounded-md px-2.5 py-1.5 transition-colors"
                title={t("tasks.address")}
              >
                <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                <span>{t("tasks.address")}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function TasksView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.tasks");
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setBusy(true);
    setError("");
    try {
      const sid = await ensureSpreadsheet();
      const data = await readAllRows(sid);
      setRows(data);
    } catch (e) {
      setError(`${t("common.error")}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = analyzeTasks(rows).filter((g) => g.rows.length > 0);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <>
      <div className="hidden sm:flex justify-end px-4 pt-2">{slider}</div>
      <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
            <i className="fa-solid fa-clipboard-list text-pink-600" aria-hidden="true" />
            {t("tasks.title")}
          </h2>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            title={t("tasks.refresh")}
          >
            <i className={`fa-solid fa-arrows-rotate ${busy ? "fa-spin" : ""}`} aria-hidden="true" />
            <span className="hidden sm:inline">{t("tasks.refresh")}</span>
          </button>
        </div>

        <div className="text-sm rounded-md px-3 py-2 flex items-start gap-2 border text-amber-800 bg-amber-50 border-amber-100">
          <i className="fa-solid fa-circle-info mt-0.5" aria-hidden="true" />
          <span>{t("tasks.intro")}</span>
        </div>

        {error && (
          <div className="text-sm rounded-md px-3 py-2 flex items-start gap-2 border text-red-700 bg-red-50 border-red-100">
            <i className="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {busy && rows.length === 0 ? (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            {t("common.processing")}
          </p>
        ) : total === 0 ? (
          <div className="text-sm rounded-md px-3 py-6 text-center border text-green-700 bg-green-50 border-green-100">
            <i className="fa-solid fa-circle-check mr-2" aria-hidden="true" />
            {t("tasks.allClear")}
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <GroupBlock key={g.key} group={g} t={t} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
